/**
 * Guest Count Selector Component
 *
 * Simple stepper component for selecting party size.
 * Immediately updates when +/- buttons are clicked.
 *
 * Props:
 * - value: Current guest count
 * - onChange: Callback when count changes (called immediately)
 * - onClose: Callback to close the selector
 * - min: Minimum guests allowed (default: 1)
 * - max: Maximum guests allowed (default: 20)
 */

import React from 'react';
import { Minus, Plus } from 'lucide-react';

function GuestCountSelector({
  value = 1,
  onChange,
  onClose: _onClose,
  min = 1,
  max = 20
}) {
  /**
   * Increment guest count and update immediately
   */
  const handleIncrement = () => {
    if (value < max) {
      onChange?.(value + 1);
    }
  };

  /**
   * Decrement guest count and update immediately
   */
  const handleDecrement = () => {
    if (value > min) {
      onChange?.(value - 1);
    }
  };

  /**
   * Get display text for guest count
   */
  const getGuestText = () => {
    return value === 1 ? '1 guest' : `${value} guests`;
  };

  return (
    <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-4 w-64">
      {/* Header */}
      <div className="mb-4">
        <h3 className="font-semibold text-gray-900">Guests</h3>
        <p className="text-xs text-gray-600 mt-1">How many people are going?</p>
      </div>

      {/* Counter Controls */}
      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
        <button
          onClick={handleDecrement}
          disabled={value <= min}
          className={`w-10 h-10 rounded-full flex items-center justify-center transition ${
            value <= min
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
              : 'bg-white border-2 border-gray-300 text-gray-700 hover:border-gray-900 hover:text-gray-900'
          }`}
          aria-label="Decrease guest count"
        >
          <Minus className="w-4 h-4" />
        </button>

        <div className="text-center">
          <div className="text-2xl font-semibold text-gray-900">{value}</div>
          <div className="text-xs text-gray-600">{getGuestText()}</div>
        </div>

        <button
          onClick={handleIncrement}
          disabled={value >= max}
          className={`w-10 h-10 rounded-full flex items-center justify-center transition ${
            value >= max
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
              : 'bg-white border-2 border-gray-300 text-gray-700 hover:border-gray-900 hover:text-gray-900'
          }`}
          aria-label="Increase guest count"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>

      {/* Info Text */}
      {value >= max && (
        <p className="text-xs text-gray-600 mt-2 text-center">
          Maximum {max} guests per booking
        </p>
      )}
    </div>
  );
}

export default GuestCountSelector;
